import React from "react";
import { CircularProgress, Popover } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";
import MobileIconButton from "../../../components/MobileIconButton";
import FloatingChatInput from "@/components/chat/FloatingChatInput";
import FloatingChatComposer from "@/components/chat/FloatingChatComposer";
import { parseChatData, formatTimestamp } from "../../../utils/chatHelpers";
import { suggestions } from "./DesktopChatArea";

const MobileChatView = ({
  selectedChatId,
  chatHistory,
  messages,
  loading,
  input,
  setInput,
  handleSend,
  handleUploadClick,
  inputRef,
  handleKeyPress,
  selectedFile,
  onRemoveFile,
  fileInputRef,
  documentInputRef,
  handleFileSelect,
  handleSuggestionClick,
  messagesScrollRef,
  setShowChatView,
  handleKebabClick,
  kebabMenuAnchor,
  onKebabClose,
  onNewChat,
  newChatLoading = false,
  onViewDetails,
  showViewDetails = false,
  onUiAction,
  voiceInputProps = {},
}) => {
  const chatTitle =
    parseChatData(
      chatHistory.find((chat) => chat.id === selectedChatId)?.title
    ).primaryTitle || "New Chat";

  const handleStartNewChat = () => {
    onKebabClose?.();
    onNewChat?.();
  };

  const handleOpenDetails = () => {
    onKebabClose?.();
    onViewDetails?.();
  };

  return (
    <div className="flex flex-col h-screen min-h-0 bg-white">
      <div className="bg-white border-b border-[#E4E7EC] px-4 py-3 grid grid-cols-[36px_1fr_36px] items-center gap-2">
        <MobileIconButton onClick={() => setShowChatView(false)} aria-label="Back to chats">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M12 19L5 12L12 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </MobileIconButton>

        <h1 className="text-[16px] font-[600] text-[#1D2739] truncate text-center min-w-0">
          {chatTitle}
        </h1>

        <MobileIconButton onClick={handleKebabClick} aria-label="Chat options">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="5" r="1.5" fill="currentColor" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" />
            <circle cx="12" cy="19" r="1.5" fill="currentColor" />
          </svg>
        </MobileIconButton>
      </div>

      <Popover
        open={Boolean(kebabMenuAnchor)}
        anchorEl={kebabMenuAnchor}
        onClose={onKebabClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            padding: "4px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(16, 24, 40, 0.1)",
            minWidth: "168px",
            border: "1px solid #E4E7EC",
          },
        }}
      >
        <button
          type="button"
          onClick={handleStartNewChat}
          disabled={newChatLoading}
          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-[13px] text-[#344054] font-medium hover:bg-[#F9FAFB] disabled:opacity-50 whitespace-nowrap"
        >
          {newChatLoading ? (
            <CircularProgress size={14} />
          ) : (
            <AddIcon sx={{ fontSize: 16, color: "#475367" }} />
          )}
          Start new chat
        </button>
        {showViewDetails ? (
          <button
            type="button"
            onClick={handleOpenDetails}
            className="w-full flex items-center px-2.5 py-2 rounded-md text-left text-[13px] text-[#344054] font-medium hover:bg-[#F9FAFB] whitespace-nowrap"
          >
            View details
          </button>
        ) : null}
      </Popover>

      <div ref={messagesScrollRef} className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden">
        {messages.length === 0 ? (
          <div className="p-10">
            <p className="text-[15px] text-[#1D2739] font-[400] mb-5">
              Hi, I'm Dockie. What can I help you with today?
            </p>
            <div className="flex flex-col gap-1">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestionClick({ title: s.label })}
                  className="flex items-center gap-3 text-left px-4 py-3 rounded-xl w-fit hover:bg-[#F9FAFB] transition-colors text-[14px] text-[#344054]"
                >
                  <span className="text-[16px]">{s.icon}</span>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-4 min-w-0 max-w-full">
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id ?? `msg-${index}`}
                message={message}
                isUser={message.role === "user"}
                formatTimestamp={formatTimestamp}
                onUiAction={onUiAction}
                disabled={voiceInputProps?.voiceActive || voiceInputProps?.loading}
              />
            ))}
            {loading &&
              !messages.some((m) => m.isStreaming) && <TypingIndicator />}
          </div>
        )}
      </div>

      <FloatingChatComposer>
        <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleFileSelect} />
        <input
          ref={documentInputRef}
          type="file"
          hidden
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileSelect}
        />
        <FloatingChatInput
          multiline
          input={input}
          setInput={setInput}
          onSend={handleSend}
          onUploadClick={handleUploadClick}
          loading={loading}
          inputRef={inputRef}
          handleKeyPress={handleKeyPress}
          selectedFile={selectedFile}
          onRemoveFile={onRemoveFile}
          {...voiceInputProps}
        />
      </FloatingChatComposer>
    </div>
  );
};

export default MobileChatView;
